import type { ContractTerm, LicenseFeeMode, LicenseFeePeriod } from "../types";

export interface LicenseFeeInput {
  mode: LicenseFeeMode;
  percentage: number;
  fixedValue?: number;
  clientPrices: number[];
  contractMonths: ContractTerm;
}

export interface LicenseFeeResult {
  value: number;
  period: LicenseFeePeriod;
  multiplier: number;
  avgPrice: number;
}

export function getFeePeriod(contractMonths: ContractTerm): LicenseFeePeriod {
  return contractMonths === 6 ? "semiannual" : "annual";
}

export function getMultiplier(contractMonths: ContractTerm): number {
  return contractMonths === 6 ? 6 : 12;
}

export function calculateLicenseFee(input: LicenseFeeInput): LicenseFeeResult {
  const { mode, percentage, fixedValue, clientPrices, contractMonths } = input;

  const multiplier = getMultiplier(contractMonths);
  const period = getFeePeriod(contractMonths);

  const avgPrice =
    clientPrices.length > 0
      ? clientPrices.reduce((sum, p) => sum + p, 0) / clientPrices.length
      : 0;

  if (mode === "fixed") {
    return {
      value: fixedValue ?? 0,
      period,
      multiplier,
      avgPrice,
    };
  }

  const value = avgPrice * multiplier * (percentage / 100);

  return {
    value: Math.round(value * 100) / 100,
    period,
    multiplier,
    avgPrice,
  };
}

export function calculateLicenseFeeFromAdjustments(
  mode: LicenseFeeMode,
  percentage: number,
  fixedValue: number | undefined,
  spaceAdjustments: Array<{ clientPrice: number; totalPrice?: number }>,
  contractMonths: ContractTerm | null,
): LicenseFeeResult {
  return calculateLicenseFee({
    mode,
    percentage,
    fixedValue,
    clientPrices: spaceAdjustments.map((a) => a.totalPrice ?? a.clientPrice),
    contractMonths: contractMonths ?? 12,
  });
}
